/**
 * Application state: the player's programs, the library they merge into, the
 * derived cache keys, and the results of the most recent check.
 *
 * The source of truth is one program per level. Programs are merged into a
 * single namespace (the rules live in `src/state/library.ts`), and level
 * results are keyed on the hashes of the definitions they depend on. Editing a
 * definition therefore invalidates exactly the levels that use it, and progress
 * is derived from those keys rather than stored as a flag.
 *
 * Two things here are worth knowing before reading further.
 *
 * The level content is fixed, so what its cases mention never changes. That is
 * analysed once, when the engine first reports for duty, and reused; only the
 * player's programs are re-read on every edit.
 *
 * Everything that crosses the worker boundary is snapshotted first. Svelte's
 * reactive proxies cannot be structured-cloned, and `postMessage` says so with
 * a DataCloneError at the worst possible moment.
 */

import { LIMITS, type Level } from '@spec';
import { chapterOf, chapterTitle, content, levelById, levels } from '../content';
import { Cancelled, Engine, describeFailure } from '../engine/client';
import type { EditorDiagnostic } from '../lib/diagnostics';
import type {
  CheckReport,
  ProgramError,
  NormalizeResult,
  ProgramReport,
  TraceResult,
} from '../engine/protocol';
import { clearReports, readReport, writeReport } from './cache';
import { definitionHashes, levelKey } from './hash';
import {
  type Diagnostic,
  type Library,
  type ParsedProgram,
  brokenDependency,
  buildLibrary,
  contextOf,
  emptyLibrary,
  readStoredPrograms,
  slotOwners,
} from './library';
import { type CaseData, type CaseGroup, analyseCases, caseSources, groupBySlot } from './slots';
import { reportVerdict } from '../lib/format';
import { type Suggestion, compareSuggestions } from '../lib/suggestions';

const PROGRAMS_KEY = 'lambda-game:programs';
const SANDBOX_KEY = 'lambda-game:sandbox';
const SOLVED_KEY = 'lambda-game:solved';
const FAILED_KEY = 'lambda-game:failed';
const REFRESH_DELAY_MS = 150;

/**
 * Every case source in the game in a fixed order: level by level, each case's
 * terms in the order the engine sees them. Asking the engine for the free names
 * of these is how the interface learns what each level's cases mention.
 */
const CASE_SOURCES: string[] = levels.flatMap((level) => level.cases.flatMap(caseSources));

export type LevelStatus =
  | 'solved'
  | 'stale'
  | 'failing'
  | 'unchecked'
  | 'unavailable'
  | 'invalid';

/** Which of the three screens is open. */
type View = 'level' | 'library' | 'sandbox';

/** What a program the player wrote means, as one value. */
interface SandboxProgram {
  /** The definitions, in the order they were written. */
  definitions: { name: string; body: string; start: number }[];
  /** Names the definitions reach that they do not define themselves. */
  references: string[];
  /** Why the program could not be read, if it could not. */
  error: ProgramError | null;
}

/** A program with nothing written in it. */
const NO_SANDBOX_PROGRAM: SandboxProgram = { definitions: [], references: [], error: null };

/** The scratch pad the player reduces terms in. */
interface SandboxState {
  /** The term to reduce. */
  term: string;
  /** The sandbox's own `name = term` definitions, as written. */
  definitions: string;
  /**
   * Whether the library is in scope.
   *
   * On, the sandbox resolves against everything the player has written, which
   * is what makes it useful for trying a definition out. Off, it is a language
   * of its own: nothing from the levels is defined, nothing of the levels is
   * suggested, and a name from a level can be defined here to mean something
   * else.
   */
  useLibrary: boolean;
}

/** How far a run of "check everything" has got. */
interface CheckProgress {
  /** One-based position of the level being checked. */
  index: number;
  total: number;
  levelId: string;
}

/** A definition as the interface shows it. */
/** A definition as a screen shows it, in the order the program defines it. */
interface LibraryEntry {
  name: string;
  body: string;
  /** The level whose slot the name is, or `''` when the player invented it. */
  levelId: string;
  /** The chapter whose file the name is written in. */
  chapterId: string;
  /** What the level asks for, as a type, or `''` for a helper. */
  signature: string;
}

/**
 * A debounced trigger.
 *
 * Three places parse while the user types, and each wants the same thing: one
 * run at the end of a pause, not one per keystroke. Calling the returned
 * function again before `run` happened moves that run to the new quiet point.
 */
function debouncer(delayMs: number, run: () => void): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return () => {
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = null;
      run();
    }, delayMs);
  };
}

export class Game {
  /** The player's file for each chapter: one program per theme. */
  programs = $state<Record<string, string>>(loadPrograms());
  /** Level id to the cache key it was last solved with. */
  solved = $state<Record<string, string>>(readStoredRecord(SOLVED_KEY));
  /** Level id to the cache key its most recent check failed on. */
  failed = $state<Record<string, string>>(readStoredRecord(FAILED_KEY));
  /** Level id to the cache key of the current dependency closure. */
  keys = $state<Record<string, string>>({});
  /** How many problems each level has, for the level list. */
  problemCounts = $state<Record<string, number>>({});

  /** The merged library. Replaced wholesale so the runes see the change. */
  library = $state<Library>(emptyLibrary());
  /** The sandbox, kept here so that leaving the screen does not lose it. */
  sandbox = $state<SandboxState>(loadSandbox());
  /**
   * What the sandbox's own definitions currently mean: the definitions, the
   * names they reach, and what went wrong if anything did. One value, because
   * these describe one program and publishing them apart let one of them lag
   * behind the others.
   */
  sandboxProgram = $state<SandboxProgram>({ ...NO_SANDBOX_PROGRAM });
  /** Names the sandbox term mentions. */
  sandboxTermNames = $state<string[]>([]);

  /**
   * One status per level, recomputed together when anything it reads changes.
   *
   * Every screen asks for some of these (the sidebar for each level, the
   * header for the count, the library for progress), and each status reads the
   * library and the keys several times. Working them out once keeps the three
   * screens from re-deriving the same answer and from drifting apart.
   */
  statuses = $derived.by(() => {
    const map: Record<string, LevelStatus> = {};
    for (const level of levels) {
      map[level.id] = this.#status(level);
    }
    return map;
  });

  view = $state<View>('level');
  activeLevelId = $state<string>(levels[0]?.id ?? '');
  report = $state<CheckReport | null>(null);
  /** The key the report describes, so a report cannot outlive its code. */
  reportKey = $state<string | null>(null);
  checking = $state(false);
  progress = $state<CheckProgress | null>(null);
  notice = $state<string | null>(null);
  /** Whether the notice describes a failure rather than a result. */
  noticeFailed = $state(false);
  engineVersion = $state<string>('');

  #engine = new Engine();
  #caseData = $state.raw<CaseData | null>(null);
  /** Which level asks for each name, which is where a name leads. */
  #slotLevel = slotOwners(content.chapters).levels;

  /** Bumped by every change to the programs; the build records what it built. */
  #revision = 0;
  #built = -1;
  #refreshing: Promise<void> | null = null;

  #stopped = false;
  /** Scheduled by every change to a program, the sandbox, or the sandbox term. */
  #scheduleRefresh = debouncer(REFRESH_DELAY_MS, () => void this.refresh());
  #scheduleSandboxParse = debouncer(REFRESH_DELAY_MS, () => void this.#parseSandbox());
  #scheduleTermParse = debouncer(REFRESH_DELAY_MS, () => void this.#parseTerm());
  #sandboxParsedSource: string | null = null;

  constructor() {
    void this.#engine
      .ready()
      .then((version) => {
        this.engineVersion = version;
        return this.refresh();
      })
      .catch((error: unknown) => {
        this.#say(`could not start the engine: ${describeFailure(error)}`, true);
      });
    if (this.sandbox.definitions.trim() !== '') {
      this.#scheduleSandboxParse();
    }
    if (this.sandbox.term.trim() !== '') {
      this.#scheduleTermParse();
    }
  }

  /** Puts a line under the level, and remembers whether it is bad news. */
  #say(message: string | null, failed = false): void {
    this.notice = message;
    this.noticeFailed = failed;
  }

  /** The level currently open in the main pane. */
  get activeLevel(): Level {
    return levelById.get(this.activeLevelId) ?? levels[0]!;
  }

  /**
   * The report for the open level, as long as it still describes the code on
   * screen. Editing a definition changes the level's key, and a verdict about
   * the previous code is worse than no verdict at all.
   */
  get currentReport(): CheckReport | null {
    const key = this.keys[this.activeLevelId];
    return this.report !== null && key !== undefined && this.reportKey === key
      ? this.report
      : null;
  }

  // --- the player's programs ---------------------------------------------

  /** The player's file for a chapter, empty when they have written none. */
  chapterProgram(chapterId: string): string {
    return this.programs[chapterId] ?? '';
  }

  /** The file a level is written in: its chapter's. */
  programFor(levelId: string): string {
    const chapter = chapterOf.get(levelId);
    return chapter === undefined ? '' : this.chapterProgram(chapter.id);
  }

  /** Stores a level's chapter file. */
  setProgramFor(levelId: string, source: string): void {
    const chapter = chapterOf.get(levelId);
    if (chapter === undefined) {
      return;
    }
    this.programs = { ...this.programs, [chapter.id]: source };
    writeStored(PROGRAMS_KEY, this.programs);
    this.#programsChanged();
  }

  /** Erases a chapter's file, and with it everything written for its tasks. */
  clearChapter(chapterId: string): void {
    if (this.chapterProgram(chapterId) === '') {
      return;
    }
    const programs = { ...this.programs };
    delete programs[chapterId];
    this.programs = programs;
    writeStored(PROGRAMS_KEY, this.programs);
    this.#programsChanged();
  }

  /** Levels that were solved against definitions that have since changed. */
  staleLevels(): string[] {
    return levels.map((level) => level.id).filter((id) => this.statuses[id] === 'stale');
  }

  /**
   * Diagnostics to show on a level: the chapter's, minus the ones that are
   * about a different task. A problem with a definition the task owns belongs
   * to the task; a syntax error belongs to the chapter, and is shown everywhere
   * in it.
   */
  diagnosticsFor(levelId: string): Diagnostic[] {
    const chapter = chapterOf.get(levelId);
    if (chapter === undefined) {
      return [];
    }
    return (this.library.diagnostics.get(chapter.id) ?? []).filter(
      (entry) => entry.levelId === null || entry.levelId === levelId,
    );
  }

  /** The definitions a level is written against, in the order they are read. */
  contextFor(levelId: string): LibraryEntry[] {
    const level = levelById.get(levelId);
    if (level === undefined) {
      return [];
    }
    // Everything the level needs that is not already in the file on screen:
    // definitions written in this chapter are visible in the editor.
    const chapter = chapterOf.get(levelId)?.id;
    return contextOf(
      [...(level.requires ?? []), ...this.#mentions(levelId)],
      this.library.definitions,
      this.library.references,
      this.library.order,
    )
      .filter((name) => this.library.owners.get(name) !== chapter)
      .map((name) => this.#entry(name));
  }

  /**
   * Everything the player has defined, in the order the program reads it.
   *
   * The order is the order the files were written in, and the rule that a name
   * has to be defined before it is used means that is also the order in which
   * each definition can be understood.
   */
  wholeLibrary(): LibraryEntry[] {
    return [...this.library.order.keys()]
      .sort((left, right) => (this.library.order.get(left) ?? 0) - (this.library.order.get(right) ?? 0))
      .map((name) => this.#entry(name));
  }

  /** Names a level needs before it can be checked. */
  missingRequirements(level: Level): string[] {
    return (level.requires ?? []).filter((name) => !this.isDefined(name));
  }

  /**
   * The first name the level depends on that did not parse, or `null`.
   *
   * The roots are what the level asks for itself: its prerequisites and the
   * names its cases mention. Everything they are defined in terms of is
   * followed from there, so a helper that stands between the two is covered.
   */
  #brokenDependency(level: Level): string | null {
    return brokenDependency(
      [...(level.requires ?? []), ...this.#mentions(level.id)],
      this.library.definitions,
      this.library.references,
      this.library.broken,
    );
  }

  /** Slots of a level that are not defined yet. */
  #missingSlots(level: Level): string[] {
    return level.slots.map((slot) => slot.name).filter((name) => !this.isDefined(name));
  }

  /**
   * Why a level cannot be checked yet, or `null` when it can.
   *
   * The interface shows this next to the Check button and the checker refuses
   * to run without it, so a level is either checkable or it says why not,
   * including when the check is asked for from the keyboard.
   */
  blockedReason(level: Level): string | null {
    if (this.diagnosticsFor(level.id).length > 0) {
      return 'compilation error';
    }
    const broken = this.#brokenDependency(level);
    if (broken !== null) {
      return `\`${broken}\` does not parse`;
    }
    // A name from an earlier level and a slot of this one are both definitions
    // the level is missing, and one list says so in one place.
    const missing = [...new Set([...this.missingRequirements(level), ...this.#missingSlots(level)])];
    if (missing.length > 0) {
      return `missing definitions: ${missing.join(', ')}`;
    }
    return null;
  }

  /**
   * The names a level's editor may suggest.
   *
   * The level's own slots come first, then everything defined in this chapter
   * or an earlier one. Nothing from a later chapter is offered: the level comes
   * before it, so it is not part of what the level is asking for, and reaching
   * forward is the one direction the game does not want to encourage.
   */
  suggestionsFor(levelId: string): Suggestion[] {
    const level = levelById.get(levelId);
    if (level === undefined) {
      return [];
    }
    const reach = this.#chapterPosition(chapterOf.get(levelId)?.id);
    const suggestions: Suggestion[] = level.slots.map((slot) => ({
      name: slot.name,
      detail: 'this level',
      rank: 0,
      defined: this.isDefined(slot.name),
    }));
    const seen = new Set(suggestions.map((entry) => entry.name));
    for (const [name, owner] of this.library.owners) {
      if (seen.has(name) || this.#chapterPosition(owner) > reach) {
        continue;
      }
      suggestions.push({ name, detail: chapterTitle(owner), rank: 1, defined: true });
    }
    return suggestions.sort(compareSuggestions);
  }

  /** The names the sandbox's editors may suggest: the whole library, and its own. */
  get sandboxSuggestions(): Suggestion[] {
    const suggestions: Suggestion[] = this.sandboxProgram.definitions.map((definition) => ({
      name: definition.name,
      detail: 'this sandbox',
      rank: 0,
      defined: true,
    }));
    if (!this.sandbox.useLibrary) {
      return suggestions.sort(compareSuggestions);
    }
    const seen = new Set(suggestions.map((entry) => entry.name));
    for (const [name, owner] of this.library.owners) {
      if (!seen.has(name)) {
        suggestions.push({ name, detail: chapterTitle(owner), rank: 1, defined: true });
      }
    }
    return suggestions.sort(compareSuggestions);
  }

  /** Every definition in the library, for a pane that shows all of it. */
  get librarySuggestions(): Suggestion[] {
    return [...this.library.owners]
      .map(([name, owner]) => ({ name, detail: chapterTitle(owner), rank: 1, defined: true }))
      .sort(compareSuggestions);
  }

  /** Where a chapter sits in teaching order, or `-1` for an unknown one. */
  #chapterPosition(chapterId: string | undefined): number {
    return content.chapters.findIndex((entry) => entry.id === chapterId);
  }

  /** Whether a name is defined anywhere in the library. */
  isDefined(name: string): boolean {
    return name in this.library.definitions;
  }

  /**
   * The level that defines a name, or that will ask for it.
   *
   * Following this is how a statement's names become links: a definition whose
   * level is already solved leads there, and a name that nobody has defined yet
   * leads to the level that asks for it, which is the level that is missing.
   */
  levelFor(name: string): string | undefined {
    const asked = this.#slotLevel.get(name);
    if (asked !== undefined) {
      return asked;
    }
    // A helper owns no task, so a reader following it is taken to the chapter
    // that has it, at its first level.
    const chapter = this.library.owners.get(name);
    return content.chapters.find((entry) => entry.id === chapter)?.levels[0]?.id;
  }

  /** The names a level's cases mention. */
  #mentions(levelId: string): string[] {
    return this.#caseData?.mentions.get(levelId) ?? [];
  }

  /** The level's own names each of its cases exercises, in slot order. */
  caseGroupsFor(level: Level): CaseGroup[] {
    const perCase = this.#caseData?.slots.get(level.id);
    if (perCase === undefined || perCase.length !== level.cases.length) {
      return [{ names: [], indices: level.cases.map((_, index) => index) }];
    }
    return groupBySlot(perCase);
  }

  // --- derived results ----------------------------------------------------

  /**
   * Status of a level under the current library.
   *
   * A verdict is only compared once the level could actually run. Its marks
   * describe the library as it was when it was checked, so a level whose own
   * definitions have since been erased holds no verdict at all; showing the
   * old one as stale would offer a check that cannot be started.
   */
  statusOf(id: string): LevelStatus {
    return this.statuses[id] ?? 'unchecked';
  }

  #status(level: Level): LevelStatus {
    if ((this.problemCounts[level.id] ?? 0) > 0) {
      return 'invalid';
    }
    // A definition this level needs that did not parse is a broken library
    // rather than a missing prerequisite, and a stale mark would say the only
    // thing that had happened was an edit.
    if (this.#brokenDependency(level) !== null) {
      return 'invalid';
    }
    if (this.missingRequirements(level).length > 0) {
      return 'unavailable';
    }
    if (this.#missingSlots(level).length > 0) {
      return 'unchecked';
    }
    const key = this.keys[level.id];
    if (key === undefined) {
      return 'unchecked';
    }
    if (this.solved[level.id] === key) {
      return 'solved';
    }
    // A check that failed on exactly this content is more informative than
    // "stale", which only says that it used to be solved.
    if (this.failed[level.id] === key) {
      return 'failing';
    }
    return this.solved[level.id] === undefined ? 'unchecked' : 'stale';
  }

  /** How many levels are solved under the current library. */
  solvedCount(): number {
    return levels.filter((level) => this.statuses[level.id] === 'solved').length;
  }

  /** How far through a chapter the player is. */
  chapterProgress(chapterId: string): { solved: number; total: number } {
    const chapter = content.chapters.find((entry) => entry.id === chapterId);
    const chapterLevels = (chapter?.levels ?? []).filter((level) => level.lesson !== true);
    return {
      solved: chapterLevels.filter((level) => this.statuses[level.id] === 'solved').length,
      total: chapterLevels.length,
    };
  }

  // --- rebuilding the library ---------------------------------------------

  /** Rebuilds the library and the cache keys after a change, debounced. */
  #programsChanged(): void {
    this.#revision += 1;
    this.#scheduleRefresh();
  }

  /**
   * Re-parses the programs and recomputes every level's cache key.
   *
   * Rebuilding is skipped when nothing has changed, which matters because this
   * is awaited once per level by "check everything". Changes made while a build
   * is running are picked up by building again, so the keys a caller sees are
   * never older than the call.
   */
  refresh(): Promise<void> {
    this.#refreshing ??= this.#rebuild().finally(() => {
      this.#refreshing = null;
    });
    return this.#refreshing;
  }

  async #rebuild(): Promise<void> {
    let target = this.#revision;
    while (this.#built !== target) {
      if (!(await this.#build(target))) {
        return;
      }
      target = this.#revision;
    }
  }

  /**
   * The fixed case data, analysed on first use.
   *
   * Throws when the engine cannot answer, in which case nothing is cached and
   * the next build asks again.
   */
  async #caseAnalysis(): Promise<CaseData> {
    if (this.#caseData === null) {
      const analysis = await this.#engine.analyze(CASE_SOURCES);
      this.#caseData = analyseCases(levels, analysis.references);
    }
    return this.#caseData;
  }

  /**
   * Reads everything a build needs.
   *
   * The programs change with every edit; the case data never does. Asking for
   * both at once means the first build costs one round trip rather than two.
   */
  async #readInputs(): Promise<{ programs: ProgramReport[]; caseData: CaseData }> {
    const chapters = content.chapters;
    const sources = chapters.map((chapter) => this.chapterProgram(chapter.id));
    const [caseData, response] = await Promise.all([
      this.#caseAnalysis(),
      this.#engine.parsePrograms(sources),
    ]);
    return { programs: response.programs, caseData };
  }

  /** Whether the library reflects the programs as they are now. */
  #isCurrent(): boolean {
    return this.#built === this.#revision;
  }

  /** Builds the library for the state as it is now. False if the request failed. */
  async #build(target: number): Promise<boolean> {
    let inputs: { programs: ProgramReport[]; caseData: CaseData };
    try {
      inputs = await this.#readInputs();
    } catch (error) {
      if (!(error instanceof Cancelled)) {
        this.#say(`the engine stopped: ${describeFailure(error)}`, true);
      }
      return false;
    }
    const { programs, caseData } = inputs;
    const chapters = content.chapters;

    const parsed: ParsedProgram[] = chapters.map((chapter, index) => {
      const report = programs[index];
      return {
        chapterId: chapter.id,
        definitions: (report?.definitions ?? []).map((definition) => ({ ...definition })),
        errors: report?.errors ?? [],
      };
    });
    const library = buildLibrary(parsed, chapters);

    const hashes = definitionHashes(library.definitions, library.references);
    const keys: Record<string, string> = {};
    for (const level of levels) {
      keys[level.id] = levelKey(
        level,
        content.version,
        caseData.mentions.get(level.id) ?? [],
        library.definitions,
        library.references,
        hashes,
      );
    }

    // Only a problem with a task's own definition makes that task invalid; a
    // mistake elsewhere in the file leaves the rest of the chapter checkable.
    const counts: Record<string, number> = {};
    for (const entries of library.diagnostics.values()) {
      for (const entry of entries) {
        if (entry.levelId !== null) {
          counts[entry.levelId] = (counts[entry.levelId] ?? 0) + 1;
        }
      }
    }

    this.problemCounts = counts;
    this.library = library;
    this.keys = keys;
    this.#built = target;
    return true;
  }

  // --- checking -----------------------------------------------------------

  /**
   * Opens a level, restoring its last report from the cache when possible.
   *
   * `undefined` arrives when the caller is already at the first or last level,
   * and is a quiet no-op rather than an error for its callers to guard.
   */
  async openLevel(id?: string): Promise<void> {
    if (id === undefined) {
      return;
    }
    this.view = 'level';
    this.activeLevelId = id;
    this.report = null;
    this.reportKey = null;
    this.#say(null);
    await this.refresh();
    const key = this.keys[id];
    if (key !== undefined) {
      const cached = await readReport(key);
      if (cached !== undefined && this.activeLevelId === id && this.keys[id] === key) {
        this.report = cached;
        this.reportKey = key;
      }
    }
  }

  /** Checks the open level, using and filling the cache. */
  async checkActiveLevel(): Promise<void> {
    await this.#checkLevel(this.activeLevelId);
  }

  /**
   * Checks one level, using and filling the cache.
   *
   * Returns whether a verdict was recorded, which is what a run of checks
   * counts: a level that could not be checked says why on its own, and the
   * run's summary must not paper over that with a count of levels.
   */
  async #checkLevel(id: string): Promise<boolean> {
    const level = levelById.get(id);
    if (level === undefined) {
      return false;
    }
    await this.refresh();
    if (!this.#isCurrent()) {
      // The library could not be rebuilt, so anything checked now would be
      // checked against code the player has already replaced. The notice from
      // the failed build is on screen.
      return false;
    }
    if (this.blockedReason(level) !== null) {
      // Nothing to run: the level says why, whether that is a syntax error, a
      // prerequisite nobody has written yet, or its own missing definitions.
      return false;
    }
    const key = this.keys[id];
    if (key === undefined) {
      // Unreachable while the library is current: every level gets a key in a
      // build. Refusing here keeps a verdict from ever being recorded against
      // nothing at all.
      return false;
    }
    const cached = await readReport(key);
    if (cached !== undefined) {
      this.#record(id, key, cached);
      if (this.activeLevelId === id) {
        this.report = cached;
        this.reportKey = key;
      }
      return true;
    }

    this.checking = true;
    this.#say(null);
    try {
      const report = await this.#engine.check(
        this.#plainDefinitions(),
        level.cases,
        level.limits ?? undefined,
      );
      await writeReport(key, report);
      this.#record(id, key, report);
      if (this.activeLevelId === id) {
        this.report = report;
        this.reportKey = key;
      }
      return true;
    } catch (error) {
      if (!(error instanceof Cancelled)) {
        this.#say(describeFailure(error), true);
      }
      return false;
    } finally {
      this.checking = false;
    }
  }

  /**
   * Forgets every recorded verdict, then checks every level that can run, in
   * order.
   *
   * Forgetting is what makes the run worth having: a level that already holds
   * a verdict would otherwise be skipped, and this is the one way to ask for
   * all of it again. Anything the level itself cannot run is skipped (no
   * definitions written, a prerequisite missing, a syntax error), because the
   * report would only repeat what the level already says.
   */
  async checkAll(): Promise<void> {
    if (!(await this.#startRun())) {
      return;
    }
    await this.#forgetResults();
    await this.#checkInOrder(
      levels.filter((level) => level.lesson !== true && this.blockedReason(level) === null),
    );
  }

  /**
   * Re-checks exactly the levels whose verdicts describe code that has since
   * changed, which is what the header's re-check button promises.
   */
  async recheckStale(): Promise<void> {
    if (!(await this.#startRun())) {
      return;
    }
    const stale = this.staleLevels()
      .map((id) => levelById.get(id))
      .filter((level) => level !== undefined)
      .filter((level) => this.blockedReason(level) === null);
    await this.#checkInOrder(stale);
  }

  /** Clears a run's state and waits for the library to settle. */
  async #startRun(): Promise<boolean> {
    // Cleared before the first await, so that a Stop pressed while the
    // programs are still being read is not forgotten when the run resumes.
    this.#stopped = false;
    this.#say(null);
    await this.refresh();
    if (this.#stopped) {
      this.#say('stopped');
      return false;
    }
    if (!this.#isCurrent()) {
      // Nothing can be checked against a library that was not rebuilt, and the
      // build has already said why.
      return false;
    }
    return true;
  }

  /** Works through `pending` in order, showing progress and a summary. */
  async #checkInOrder(pending: Level[]): Promise<void> {
    let checked = 0;
    let undecided = 0;
    try {
      for (const level of pending) {
        if (this.#stopped) {
          break;
        }
        this.progress = { index: checked + undecided + 1, total: pending.length, levelId: level.id };
        if (await this.#checkLevel(level.id)) {
          checked += 1;
        } else {
          undecided += 1;
        }
      }
    } finally {
      this.progress = null;
    }
    this.#say(this.#summary(checked, undecided, pending.length));
  }

  /** What to say once a run of checks has finished, or been stopped. */
  #summary(checked: number, undecided: number, total: number): string {
    if (this.#stopped) {
      const visited = checked + undecided;
      return visited === 0 ? 'stopped' : `stopped after ${visited} of ${total}`;
    }
    if (total === 0) {
      return 'nothing left to check';
    }
    if (checked === 0) {
      // No verdict at all came out of the run, and whatever went wrong is
      // already on screen; it explains more than a count would.
      return this.notice ?? 'nothing was checked';
    }
    return undecided === 0
      ? `checked ${checked} level${checked === 1 ? '' : 's'}`
      : `checked ${checked} of ${total}; ${undecided} could not be checked`;
  }

  /** Stops a run of checks. */
  cancel(): void {
    this.#stopped = true;
    this.progress = null;
    this.#engine.cancel();
    this.checking = false;
  }

  // --- the sandbox --------------------------------------------------------

  /**
   * The library definitions the sandbox's term is written against, dependencies
   * first.
   *
   * The whole library is always in scope (there is nothing to switch on, and
   * the sandbox's own definitions cannot take a library name), but showing all
   * of it would bury the two or three definitions that matter, so the pane
   * shows what the term and the sandbox's own definitions reach, the way a
   * level shows what its cases reach.
   */
  get sandboxContext(): LibraryEntry[] {
    if (!this.sandbox.useLibrary) {
      return [];
    }
    return contextOf(
      [...this.sandboxTermNames, ...this.sandboxProgram.references],
      this.library.definitions,
      this.library.references,
      this.library.order,
    ).map((name) => this.#entry(name));
  }

  /**
   * What is wrong with the sandbox's definitions, if anything.
   *
   * The parse error is what the engine reported; a name taken from the library
   * is worked out here, because the library changes under the sandbox and a
   * program is not wrong until it is read against the library as it is now.
   */
  get sandboxProblems(): EditorDiagnostic[] {
    const problem = this.#programProblem(this.sandboxProgram);
    return problem === null ? [] : [problem];
  }

  /** The one wrong thing about a parsed sandbox program, if there is any. */
  #programProblem(program: SandboxProgram): ProgramError | null {
    return program.error ?? this.#shadowProblem(program.definitions);
  }

  /** Changes part of the sandbox, and remembers it. */
  setSandbox(patch: Partial<SandboxState>): void {
    this.sandbox = { ...this.sandbox, ...patch };
    writeStored(SANDBOX_KEY, this.sandbox);
    if (patch.definitions !== undefined) {
      this.#scheduleSandboxParse();
    }
    if (patch.term !== undefined) {
      this.#scheduleTermParse();
    }
  }

  /** Reduces a term against the sandbox. */
  async normalizeInSandbox(term: string): Promise<NormalizeResult> {
    const local = await this.#sandboxLocalDefinitions();
    if (local.error !== null) {
      return {
        normalForm: null,
        limit: null,
        error: `in the definitions: ${local.error.message}`,
      };
    }
    return this.#engine.normalize(this.#sandboxEnvironment(local.definitions), term, {
      budget: LIMITS,
      allowUnknownNames: true,
    });
  }

  /** Steps through a term against the sandbox. */
  async traceInSandbox(term: string, steps: number): Promise<TraceResult> {
    const local = await this.#sandboxLocalDefinitions();
    if (local.error !== null) {
      return {
        terms: [],
        completed: false,
        limit: null,
        error: `in the definitions: ${local.error.message}`,
      };
    }
    return this.#engine.trace(this.#sandboxEnvironment(local.definitions), term, steps, {
      budget: LIMITS,
      allowUnknownNames: true,
    });
  }

  /**
   * The sandbox's definitions as they are written now.
   *
   * The debounced parse is only there to keep the editor's completions and
   * error message fresh; a reduction always reads the source, so pressing
   * Normalise right after typing cannot run against an older program.
   */
  async #sandboxLocalDefinitions(): Promise<{
    definitions: Record<string, string>;
    error: ProgramError | null;
  }> {
    const program =
      this.#sandboxParsedSource === this.sandbox.definitions
        ? this.sandboxProgram
        : await this.#parseSandbox();
    const error = this.#programProblem(program);
    if (error !== null) {
      return { definitions: {}, error };
    }
    const definitions: Record<string, string> = {};
    for (const definition of program.definitions) {
      definitions[definition.name] = definition.body;
    }
    return { definitions, error: null };
  }

  /**
   * Reads the free names out of the sandbox term, for the library pane.
   *
   * A term that does not parse yet (the middle of typing one, usually) has no
   * names to speak of, which is what the engine reports and what the pane then
   * shows.
   */
  async #parseTerm(): Promise<void> {
    const term = this.sandbox.term;
    if (term.trim() === '') {
      this.sandboxTermNames = [];
      return;
    }
    try {
      const analysis = await this.#engine.analyze([term]);
      if (this.sandbox.term === term) {
        this.sandboxTermNames = analysis.references[0] ?? [];
      }
    } catch {
      // The engine is gone; the pane empties rather than lying.
      this.sandboxTermNames = [];
    }
  }

  /**
   * Parses the sandbox's definitions, and publishes them if they are still the
   * ones on screen.
   *
   * A parse that the engine never answered (because the worker was restarted
   * under it) publishes nothing and says so, rather than pretending the
   * program is empty: the next attempt parses it again.
   */
  async #parseSandbox(): Promise<SandboxProgram> {
    const source = this.sandbox.definitions;
    let parsed: SandboxProgram;
    try {
      parsed = await this.#readProgram(source);
    } catch (error) {
      if (error instanceof Cancelled) {
        return {
          ...NO_SANDBOX_PROGRAM,
          error: { message: 'the engine restarted; try again', start: 0, end: 0 },
        };
      }
      parsed = {
        ...NO_SANDBOX_PROGRAM,
        error: { message: String(error), start: 0, end: 0 },
      };
    }
    // Only the newest source may publish its result; an older parse that lands
    // late must not overwrite what the player has typed since. Everything that
    // describes the program is published at once, so no part of it can be left
    // describing the program before last.
    if (this.sandbox.definitions === source) {
      this.#sandboxParsedSource = source;
      this.sandboxProgram = parsed;
    }
    return parsed;
  }

  /**
   * Reads a standalone program.
   *
   * Nothing here touches the store: a parse that nobody publishes (because the
   * source moved on, or because the engine never answered) has to leave the
   * published program alone.
   */
  async #readProgram(source: string): Promise<SandboxProgram> {
    if (source.trim() === '') {
      return { ...NO_SANDBOX_PROGRAM };
    }
    const response = await this.#engine.parsePrograms([source]);
    const program = response.programs[0];
    const references = new Set<string>();
    const definitions = (program?.definitions ?? []).map((entry) => {
      for (const reference of entry.references) {
        references.add(reference);
      }
      return { name: entry.name, body: entry.body, start: entry.start };
    });
    return {
      definitions,
      references: [...references],
      error: program?.errors[0] ?? null,
    };
  }

  /**
   * The first sandbox definition that would take a library name, if any.
   *
   * A namespace has one definition per name, in the sandbox as much as in a
   * chapter: without this the sandbox's definition would quietly replace the
   * library's, and every name in the pane that the term did not reach would
   * look like it had never been there.
   */
  #shadowProblem(
    definitions: readonly { name: string; start: number }[],
  ): ProgramError | null {
    if (!this.sandbox.useLibrary) {
      return null;
    }
    for (const definition of definitions) {
      const chapter = this.library.owners.get(definition.name);
      if (chapter === undefined) {
        continue;
      }
      const title = chapterTitle(chapter);
      const where = title === undefined ? 'the library' : `the ${title} chapter`;
      return {
        message: `\`${definition.name}\` is already defined in ${where}, so it cannot be defined here as well`,
        start: definition.start,
        end: definition.start + definition.name.length,
      };
    }
    return null;
  }

  /**
   * The environment a sandbox term resolves against: the whole library, with
   * the sandbox's own definitions on top. A local definition can never collide
   * with a library name, which is reported as an error, so this only ever adds
   * names the library does not have.
   */
  #sandboxEnvironment(local: Record<string, string>): Record<string, string> {
    return this.sandbox.useLibrary ? { ...this.#plainDefinitions(), ...local } : local;
  }

  // --- erasing ------------------------------------------------------------

  /** Clears the recorded verdicts and every cached report. */
  async #forgetResults(): Promise<void> {
    this.solved = {};
    this.failed = {};
    writeStored(SOLVED_KEY, this.solved);
    writeStored(FAILED_KEY, this.failed);
    await clearReports();
  }

  /** Erases everything, including the programs themselves. */
  async resetEverything(): Promise<void> {
    this.programs = {};
    writeStored(PROGRAMS_KEY, this.programs);
    await this.#forgetResults();
    this.#programsChanged();
    await this.refresh();
  }

  // --- internals ----------------------------------------------------------

  #record(id: string, key: string, report: CheckReport): void {
    if (reportVerdict(report) === 'OK') {
      this.solved = { ...this.solved, [id]: key };
      if (id in this.failed) {
        const failed = { ...this.failed };
        delete failed[id];
        this.failed = failed;
      }
    } else {
      this.failed = { ...this.failed, [id]: key };
    }
    writeStored(SOLVED_KEY, this.solved);
    writeStored(FAILED_KEY, this.failed);
  }

  #entry(name: string): LibraryEntry {
    // A helper has no level: the player invented it, and saying so is what
    // lets the library screen leave the level link off the row.
    const levelId = this.#slotLevel.get(name) ?? '';
    const level = levelId === '' ? undefined : levelById.get(levelId);
    return {
      name,
      body: this.library.definitions[name] ?? '',
      levelId,
      chapterId: this.library.owners.get(name) ?? '',
      signature: level?.slots.find((slot) => slot.name === name)?.signature ?? '',
    };
  }

  /**
   * The library as plain data. A worker cannot receive Svelte's reactive
   * proxies: `postMessage` fails with a DataCloneError, so everything that
   * crosses the boundary is snapshotted on the way out.
   */
  #plainDefinitions(): Record<string, string> {
    return $state.snapshot(this.library.definitions) as Record<string, string>;
  }
}

/**
 * Reads stored programs, moving a per-level save into per-chapter files.
 *
 * A level id as a key is what the old shape looks like; chapter ids are words,
 * so the two cannot be confused. The migration leaves every solved mark alone,
 * because a level's cache key is made of the definitions it depends on rather
 * than of the program they were written in.
 */
function loadPrograms(): Record<string, string> {
  const stored = readStoredRecord(PROGRAMS_KEY);
  const programs = readStoredPrograms(stored, content.chapters);
  if (programs !== stored) {
    writeStored(PROGRAMS_KEY, programs);
  }
  return programs;
}

/** Reads a stored object, ignoring anything that is not a string map. */
function readStoredRecord(key: string): Record<string, string> {
  const raw = readStored<unknown>(key, null);
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return {};
  }
  const entries = Object.entries(raw).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  );
  return Object.fromEntries(entries);
}

/** Reads the sandbox, which is a scratch pad and never worth failing over. */
function loadSandbox(): SandboxState {
  const raw = readStored<Partial<SandboxState> | null>(SANDBOX_KEY, null);
  return {
    term: typeof raw?.term === 'string' ? raw.term : '',
    definitions: typeof raw?.definitions === 'string' ? raw.definitions : '',
    useLibrary: typeof raw?.useLibrary === 'boolean' ? raw.useLibrary : true,
  };
}

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage may be full or disabled; the session still works in memory.
  }
}

export const game = new Game();
